import { CardMenuList, MenuItem } from "UIPalette/CardMenuList/CardMenuList";
import { FloatingPopover } from "UIPalette/FloatingPopover/FloatingPopover";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "shared/UI/Buttons/Button";
import { useGetLearningMaterialFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { AudioPlayer } from "src/Views/SupportMoments/Components/AudioPlayer/AudioPlayer";
import { PositiveOutcomeScreen } from "src/Views/SupportMoments/Components/PositiveOutcomeScreen/PositiveOutcomeScreen";
import { NeutralOutcomeScreen } from "src/Views/SupportMoments/Components/NeutralOutcomeScreen/NeutralOutcomeScreen";
import { NegativeOutcomeScreen } from "src/Views/SupportMoments/Components/NegativeOutcomeScreen/NegativeOutcomeScreen";
import { getResourceFilterForFeeling, SUPPORT_OPTION_FEELING_MAP, SupportOption } from "src/Views/SupportMoments/enums/supportMoments/supportMoments";
import { ReactComponent as PensiveFaceEmoji } from "shared/assets/Icons/emoji-pensive-face.svg";
import { ReactComponent as SleepingFaceEmoji } from "shared/assets/Icons/emoji-sleeping-face.svg";
import { ReactComponent as AnxiousFaceEmoji } from "shared/assets/Icons/emoji-anxious-face.svg";
import { ReactComponent as ThinkingFaceEmoji } from "shared/assets/Icons/emoji-thinking-face.svg";

const AUDIO_PLAYER_DEMO_IMAGE_URL = "https://www.datocms-assets.com/93767/1758540706-1758095144-students_mental_health.webp";
const AUDIO_PLAYER_DEMO_RESOURCE_ID = "SqX-uNfjSca4iytMMla8Jw";

type EmotionOption = {
  emoji: string;
  label: string;
};

const EMOTION_OPTIONS: EmotionOption[] = [
  { emoji: "😰", label: "Overwhelmed" },
  { emoji: "😟", label: "Anxious" },
  { emoji: "😫", label: "Exhausted" },
  { emoji: "😞", label: "Feeling low" },
  { emoji: "🤷", label: "Not sure" },
  { emoji: "💬", label: "Just talk" },
];

const EmotionButton = ({ option, selected, onClick }: { option: EmotionOption; selected?: boolean; onClick?: () => void }) => {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-2 px-4 py-3 rounded-lg border-2 transition ${
        selected ? "border-brand-purple bg-brand-purple text-white" : "border-brand-grey-light bg-white text-font-dark hover:border-brand-grey"
      }`}
    >
      <span className="text-3xl">{option.emoji}</span>
      <span className="text-xs font-medium">{option.label}</span>
    </button>
  );
};

const DemoCase = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className="rounded-lg border border-brand-grey-light bg-xl-sky-grey p-6">
      <h3 className="font-bold text-font-dark mb-4">{title}</h3>
      <div className="rounded-md bg-white p-6">{children}</div>
    </div>
  );
};

export const ComponentsSupportMoments = () => {
  const [selectedEmotion, setSelectedEmotion] = useState<string | null>(null);
  const [selectedFrequency, setSelectedFrequency] = useState<string | null>(null);
  const [isAudioPlayerOpen, setIsAudioPlayerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isPositiveOutcomeOpen, setIsPositiveOutcomeOpen] = useState(false);
  const [isAnxietyPositiveOutcomeOpen, setIsAnxietyPositiveOutcomeOpen] = useState(false);
  const [isNeutralOutcomeOpen, setIsNeutralOutcomeOpen] = useState(false);
  const [isAnxietyNeutralOutcomeOpen, setIsAnxietyNeutralOutcomeOpen] = useState(false);
  const [isNegativeOutcomeOpen, setIsNegativeOutcomeOpen] = useState(false);
  const [isAnxietyNegativeOutcomeOpen, setIsAnxietyNegativeOutcomeOpen] = useState(false);
  const navigate = useNavigate();
  const anxiousResourceFilter = getResourceFilterForFeeling(SUPPORT_OPTION_FEELING_MAP[SupportOption.Anxious]!);
  const {
    data: audioPlayerArticle,
    isLoading: isAudioPlayerArticleLoading,
    error: audioPlayerArticleError,
  } = useGetLearningMaterialFunctionRequest({ id: AUDIO_PLAYER_DEMO_RESOURCE_ID }, { revalidateOnMountIfStale: false, showErrorIfStale: false });

  const menuItems: MenuItem[] = [
    {
      emoji: PensiveFaceEmoji,
      title: "Overwhelmed",
      theme: "green",
      onClick: () => setSelectedItem("/overwhelmed"),
    },
    {
      emoji: SleepingFaceEmoji,
      title: "Exhausted",
      theme: "purple",
      onClick: () => setSelectedItem("/exhausted"),
    },
    {
      emoji: AnxiousFaceEmoji,
      title: "Anxious",
      theme: "orange",
      onClick: () => setSelectedItem("/anxious"),
    },
    {
      emoji: ThinkingFaceEmoji,
      title: "Just checking things out",
      theme: "red",
      onClick: () => setSelectedItem("/checkingout"),
    },
  ];

  if (isPositiveOutcomeOpen) {
    return <PositiveOutcomeScreen onFinish={() => setIsPositiveOutcomeOpen(false)} onLeave={() => {}} />;
  }

  if (isAnxietyPositiveOutcomeOpen) {
    return (
      <PositiveOutcomeScreen resourceFilter={anxiousResourceFilter} onFinish={() => setIsAnxietyPositiveOutcomeOpen(false)} onLeave={() => {}} />
    );
  }

  if (isNeutralOutcomeOpen) {
    return (
      <NeutralOutcomeScreen
        onTryAnotherResource={() => setIsNeutralOutcomeOpen(false)}
        onFinish={() => setIsNeutralOutcomeOpen(false)}
        onLeave={() => {}}
      />
    );
  }

  if (isAnxietyNeutralOutcomeOpen) {
    return (
      <NeutralOutcomeScreen
        resourceFilter={anxiousResourceFilter}
        onTryAnotherResource={() => setIsAnxietyNeutralOutcomeOpen(false)}
        onFinish={() => setIsAnxietyNeutralOutcomeOpen(false)}
        onLeave={() => {}}
      />
    );
  }

  if (isNegativeOutcomeOpen) {
    return <NegativeOutcomeScreen onFinish={() => setIsNegativeOutcomeOpen(false)} />;
  }

  if (isAnxietyNegativeOutcomeOpen) {
    return <NegativeOutcomeScreen resourceFilter={anxiousResourceFilter} onFinish={() => setIsAnxietyNegativeOutcomeOpen(false)} />;
  }

  return (
    <div className="space-y-8">
      <CardMenuList menuItems={menuItems} />
      <p>Selected Item: {selectedItem}</p>
      <Button className="max-w-[300px]" onClick={() => navigate("/support-moments")} text="Open Support Moments" />

      {/* Support Moments outcome screens */}
      <DemoCase title="Outcome screens">
        <Button className="max-w-[300px]" onClick={() => setIsPositiveOutcomeOpen(true)} text="A bit better" />
        <Button className="mt-4 max-w-[300px]" onClick={() => setIsAnxietyPositiveOutcomeOpen(true)} text="A bit better (anxiety)" />
        <Button className="mt-4 max-w-[300px]" onClick={() => setIsNeutralOutcomeOpen(true)} text="About the same" />
        <Button className="mt-4 max-w-[300px]" onClick={() => setIsAnxietyNeutralOutcomeOpen(true)} text="About the same (anxiety)" />
        <Button className="mt-4 max-w-[300px]" onClick={() => setIsNegativeOutcomeOpen(true)} text="Worse" />
        <Button className="mt-4 max-w-[300px]" onClick={() => setIsAnxietyNegativeOutcomeOpen(true)} text="Worse (anxiety)" />
      </DemoCase>

      <div>
        <h1 className="text-3xl font-bold text-font-dark">FloatingPopover Demo</h1>
      </div>

      {/* Mobile Audio Player */}
      <DemoCase title="Mobile Audio Player">
        <Button
          className="max-w-[300px]"
          onClick={() => setIsAudioPlayerOpen(true)}
          text="Open Audio Player"
          disabled={isAudioPlayerArticleLoading || !audioPlayerArticle}
        />
        {audioPlayerArticleError && <p className="mt-2 text-sm text-brand-red">Failed to load demo audio resource.</p>}
        {isAudioPlayerOpen && audioPlayerArticle && (
          <AudioPlayer
            resource={{
              title: audioPlayerArticle.title,
              imageUrl: AUDIO_PLAYER_DEMO_IMAGE_URL,
              src: audioPlayerArticle.link ?? "",
            }}
            onClose={() => setIsAudioPlayerOpen(false)}
            onDone={() => setIsAudioPlayerOpen(false)}
          />
        )}
      </DemoCase>

      {/* Case 1 */}
      <DemoCase title="Case 1: Basic Selection">
        <FloatingPopover
          trigger={(props) => (
            <button
              ref={props.ref}
              onClick={props.onClick}
              className="rounded-lg bg-primary-700 px-4 py-2 text-white hover:bg-primary-600 transition"
            >
              Open
            </button>
          )}
        >
          {({ dismiss }) => (
            <div>
              <h4 className="font-semibold text-font-dark">Select an option</h4>
              <div className="grid grid-cols-2 gap-3">
                {EMOTION_OPTIONS.map((option) => (
                  <EmotionButton
                    key={option.label}
                    option={option}
                    onClick={() => {
                      setSelectedEmotion(option.label);
                      dismiss();
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </FloatingPopover>
        {selectedEmotion && <p className="mt-4 text-sm text-sky-grey">Selected: {selectedEmotion}</p>}
      </DemoCase>

      {/* Case 2 */}
      <DemoCase title="Case 2: Multi-step">
        <FloatingPopover
          trigger={(props) => (
            <button
              ref={props.ref}
              onClick={props.onClick}
              className="rounded-lg bg-brand-green px-4 py-2 text-white hover:bg-brand-green-light transition"
            >
              Open
            </button>
          )}
        >
          {({ dismiss }) => (
            <div>
              <h4 className="font-semibold text-font-dark">Step 1 of 2</h4>
              <div className="grid grid-cols-2 gap-3">
                {EMOTION_OPTIONS.slice(0, 4).map((option) => (
                  <EmotionButton key={option.label} option={option} />
                ))}
              </div>
              <div className="flex items-center justify-between border-t border-brand-grey-light pt-3">
                <span className="text-xs text-sky-grey">1 of 2</span>
                <div className="space-x-2">
                  <button onClick={dismiss} className="rounded px-3 py-1 text-sm text-sky-grey hover:bg-xl-sky-grey">
                    Skip
                  </button>
                  <button className="rounded bg-brand-purple px-4 py-1 text-sm text-white hover:bg-brand-purple-light">Next</button>
                </div>
              </div>
            </div>
          )}
        </FloatingPopover>
      </DemoCase>

      {/* Case 3 */}
      <DemoCase title="Case 3: Tall Content (Scrollable)">
        <div>
          <FloatingPopover
            trigger={(props) => (
              <button
                ref={props.ref}
                onClick={props.onClick}
                className="rounded-lg bg-primary-600 px-4 py-3 text-white hover:bg-primary-700 font-medium transition"
              >
                Open Tall Popover
              </button>
            )}
          >
            {({ dismiss }) => (
              <div>
                <h4 className="font-semibold text-font-dark">Long Content</h4>
                <div className="space-y-2 text-sm text-sky-grey">
                  <p>This popover contains a lot of content.</p>
                  <p>When there isn't enough vertical space, the content will scroll internally while the popover stays in its computed position.</p>
                  <p>This preserves the spotlight effect and maintains the focus on the trigger element.</p>
                  <p>Line 1: Lorem ipsum dolor sit amet.</p>
                  <p>Line 2: Consectetur adipiscing elit.</p>
                  <p>Line 3: Sed do eiusmod tempor incididunt.</p>
                  <p>Line 4: Ut labore et dolore magna aliqua.</p>
                  <p>Line 5: Ut enim ad minim veniam.</p>
                  <p>Line 6: Quis nostrud exercitation ullamco.</p>
                  <p>Line 7: Laboris nisi ut aliquip ex ea commodo.</p>
                  <p>Line 8: Consequat duis aute irure dolor.</p>
                </div>
                <button
                  onClick={dismiss}
                  className="w-full rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition"
                >
                  Close
                </button>
              </div>
            )}
          </FloatingPopover>
        </div>
      </DemoCase>

      {/* Case 4 */}
      <DemoCase title="Case 4: Pre-selected State">
        <FloatingPopover
          trigger={(props) => (
            <button
              ref={props.ref}
              onClick={props.onClick}
              className="rounded-lg bg-brand-purple px-4 py-2 text-white hover:bg-brand-purple-light transition"
            >
              Open
            </button>
          )}
        >
          {({ dismiss }) => (
            <div>
              <h4 className="font-semibold text-font-dark">Current selection</h4>
              <div className="grid grid-cols-2 gap-3">
                {EMOTION_OPTIONS.map((option) => (
                  <EmotionButton key={option.label} option={option} selected={option.label === "Overwhelmed"} />
                ))}
              </div>
              <button
                onClick={dismiss}
                className="w-full rounded bg-xl-sky-grey px-4 py-2 text-sm font-medium text-font-dark hover:bg-l-sky-grey transition"
              >
                Update
              </button>
            </div>
          )}
        </FloatingPopover>
      </DemoCase>

      {/* Case 5 */}
      <DemoCase title="Case 5: Multiple Popovers">
        <div className="grid grid-cols-3 gap-4">
          <FloatingPopover
            trigger={(props) => (
              <button
                ref={props.ref}
                onClick={props.onClick}
                className="rounded-lg bg-accent-600 px-3 py-2 text-sm text-white hover:bg-accent-700 transition w-full"
              >
                Option 1
              </button>
            )}
          >
            <div className="space-y-3">
              <h4 className="font-semibold text-font-dark">Content</h4>
              <p className="text-sm text-sky-grey">This is popover content for option 1.</p>
              <button className="w-full rounded bg-accent-600 px-3 py-2 text-xs text-white hover:bg-accent-700 transition">Action</button>
            </div>
          </FloatingPopover>

          <FloatingPopover
            trigger={(props) => (
              <button
                ref={props.ref}
                onClick={props.onClick}
                className="rounded-lg bg-brand-red px-3 py-2 text-sm text-white hover:bg-brand-red-lighter transition w-full"
              >
                Option 2
              </button>
            )}
          >
            <div className="space-y-3">
              <h4 className="font-semibold text-font-dark">Content</h4>
              <p className="text-sm text-sky-grey">This is popover content for option 2.</p>
              <button className="w-full rounded bg-brand-red px-3 py-2 text-xs text-white hover:bg-brand-red-lighter transition">Action</button>
            </div>
          </FloatingPopover>

          <FloatingPopover
            trigger={(props) => (
              <button
                ref={props.ref}
                onClick={props.onClick}
                className="rounded-lg bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-500 transition w-full"
              >
                Option 3
              </button>
            )}
          >
            <div className="space-y-3">
              <h4 className="font-semibold text-font-dark">Content</h4>
              <p className="text-sm text-sky-grey">This is popover content for option 3.</p>
              <button className="w-full rounded bg-primary-600 px-3 py-2 text-xs text-white hover:bg-primary-500 transition">Action</button>
            </div>
          </FloatingPopover>
        </div>
      </DemoCase>

      {/* Case 6 */}
      <DemoCase title="Case 6: Form with Actions">
        <FloatingPopover
          trigger={(props) => (
            <button ref={props.ref} onClick={props.onClick} className="rounded-lg bg-scarlet px-4 py-2 text-white hover:bg-m-red transition">
              Open
            </button>
          )}
        >
          {({ dismiss }) => (
            <div className="space-y-4">
              <h4 className="font-semibold text-font-dark">Choose frequency</h4>
              <div className="space-y-2">
                {["Daily", "Twice a week", "Weekly"].map((option) => (
                  <button
                    key={option}
                    onClick={() => setSelectedFrequency(option)}
                    className={`w-full rounded border-2 px-3 py-2 text-sm font-medium transition ${
                      selectedFrequency === option
                        ? "border-primary-700 bg-primary-50 text-primary-700"
                        : "border-brand-grey-light bg-white text-font-dark hover:bg-xl-sky-grey"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 border-t border-brand-grey-light pt-3">
                <button
                  onClick={() => {
                    setSelectedFrequency(null);
                    dismiss();
                  }}
                  className="flex-1 rounded bg-xl-sky-grey px-3 py-2 text-sm font-medium text-font-dark hover:bg-l-sky-grey transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    dismiss();
                  }}
                  className="flex-1 rounded bg-primary-700 px-3 py-2 text-sm font-medium text-white hover:bg-primary-600 transition"
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </FloatingPopover>
      </DemoCase>
    </div>
  );
};
